import React, { useRef, useState, useEffect } from 'react';
import SignaturePad from 'react-signature-canvas';
import { Box, Button, TextField, Typography, Grid, Paper, IconButton, Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUpdateWorkPermitMutation, useGetWorkPermitQuery, useCreateWorkPermitMutation } from '../features/schedule/workPermitSlice';
import { useLazyGetWorkPermitReportQuery } from '../features/schedule/scheduleSlice';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import dayjs from 'dayjs';

interface Worker {
  name: string;
  idNumber: string;
  idExpiry: string;
  sign: string;
  signUrl?: string;
}

interface StandbyWorker {
  name: string;
  trainingDate: string;
  duty: string;
  sign: string;
  signUrl?: string;
}

interface RescueWorker {
  name: string;
  trainingDate: string;
  duty: string;
  sign: string;
  signUrl?: string;
}

interface AirTest {
  date: string;
  deviceModel: string;
  deviceSerial: string;
  deviceValid: string;
  locations: {
    place: string;
    time: string;
    result: {
      oxygen: string;
      lowerLimit: string;
      h2s: string;
      co: string;
    };
  }[];
  responsibleName: string;
  sign: string;
  signUrl?: string;
}

const defaultStandbyDuty = `✓ 與密閉空間內的工人保持聯絡，及在緊急情況下召喚協助；\n✓ 發生緊急事故時，須向到達的救援人員匯報該次事故的有關情況；\n✓ 備齊緊急器具，候命人員亦不應進入該密閉空間。`;
const defaultRescueDuty = `✓ 已熟知緊急救援計劃的詳細資料；\n✓ 已熟知如何正確操作所有提供的救援裝備。`;

const WorkPermitPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // 從路由狀態獲取參數
  const { scheduleID, mode, formData, entry, project, position } = location.state || {};

  // 檢查是否為查看模式
  const isViewMode = mode === 'view';

  // 基本資料
  const [site, setSite] = useState('');
  const [content, setContent] = useState('');
  const [contractor, setContractor] = useState('');
  const [approver, setApprover] = useState('');
  const [riskReportDate, setRiskReportDate] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [entryTime, setEntryTime] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [expiryTime, setExpiryTime] = useState('');
  const [maxStayHour, setMaxStayHour] = useState('');

  // 將純 base64 字符串轉換為 data URL 用於顯示圖片
  const base64ToDataURL = (base64String: string): string => {
    if (!base64String) return '';
    // 如果已經是 data URL 格式，直接返回
    if (base64String.startsWith('data:')) return base64String;
    // 否則添加 data URL 前綴
    return `data:image/png;base64,${base64String}`;
  };

  const [updateWorkPermit] = useUpdateWorkPermitMutation();
  const [createWorkPermit] = useCreateWorkPermitMutation();

  // 添加下載功能
  const [getWorkPermitReport] = useLazyGetWorkPermitReportQuery();

  // 如果是編輯模式或查看模式，獲取現有數據
  const { data: existingWorkPermit, isLoading: isLoadingWorkPermit } = useGetWorkPermitQuery(
    scheduleID || '',
    { skip: !scheduleID || (mode !== 'edit' && mode !== 'view') ,  refetchOnMountOrArgChange: true},
  );

  // 處理傳入的數據
  useEffect(() => {
    if (mode === 'create' && formData) {
      // 如果是新建模式且有表單數據，預填寫基本資料
      setSite(formData.site || '');
      setContent(formData.content || '');
      setContractor(formData.contractor || '');
    } else if ((mode === 'edit' || mode === 'view') && existingWorkPermit) {
      // 如果是編輯模式或查看模式且有現有數據，填充表單
      //console.log('載入現有工作許可證數據:', existingWorkPermit);

      // 處理基本資料
      setSite(existingWorkPermit.site || '');
      setContent(existingWorkPermit.content || '');
      setContractor(existingWorkPermit.contractor || '');
      setApprover(existingWorkPermit.approver || '');
      setMaxStayHour(existingWorkPermit.maxStayHour || '');

      // 處理日期時間
      if (existingWorkPermit.riskReportDate) {
        const date = new Date(existingWorkPermit.riskReportDate);
        setRiskReportDate(date.toISOString().slice(0, 16)); // 轉換為 datetime-local 格式
      }

      if (existingWorkPermit.entryDate) {
        setEntryDate(existingWorkPermit.entryDate);
      }
      if (existingWorkPermit.entryTime) {
        setEntryTime(existingWorkPermit.entryTime);
      }

      if (existingWorkPermit.expiryDate) {
        setExpiryDate(existingWorkPermit.expiryDate);
      }

      if (existingWorkPermit.expiryTime) {
        setExpiryTime(existingWorkPermit.expiryTime);
      }

      // 處理工人資料
      if (existingWorkPermit.workers) {
        setWorkers(existingWorkPermit.workers.map((worker: any) => ({
          ...worker,
          idExpiry: worker.idExpiry ? new Date(worker.idExpiry).toISOString().split('T')[0] : ''
        })));
      }

      // 處理候命人員資料
      if (existingWorkPermit.standbyWorkers) {
        setStandbyWorkers(existingWorkPermit.standbyWorkers.map((worker: any) => ({
          ...worker,
          trainingDate: worker.trainingDate ? new Date(worker.trainingDate).toISOString().split('T')[0] : ''
        })));
      }

      // 處理救援人員資料
      if (existingWorkPermit.rescueWorkers) {
        setRescueWorkers(existingWorkPermit.rescueWorkers.map((worker: any) => ({
          ...worker,
          trainingDate: worker.trainingDate ? new Date(worker.trainingDate).toISOString().split('T')[0] : ''
        })));
      }

      // 處理空氣測試資料
      if (existingWorkPermit.airTests) {
        setAirTests(existingWorkPermit.airTests.map((test: any) => ({
          ...test,
          date: test.date ? new Date(test.date).toISOString().split('T')[0] : '',
          deviceValid: test.deviceValid ? new Date(test.deviceValid).toISOString().split('T')[0] : '',
          locations: test.locations?.map((location: any) => ({
            ...location,
            time: location.time || '', // 直接使用時間字符串格式 "14:30"
            result: { ...location.result } // 確保 result 對象也被深拷貝
          })) || []
        })));
      }

      // 處理聲明資料
      if (existingWorkPermit.declarations) {
        setDeclarations(existingWorkPermit.declarations.map((declaration: any) => ({
          ...declaration,
          validDate: declaration.validDate ? new Date(declaration.validDate).toISOString().split('T')[0] : '',
          validTime: declaration.validTime || '', // 直接使用時間字符串格式 "14:30"
          date: declaration.date ? new Date(declaration.date).toISOString().slice(0, 16) : '' // 保持 ISO 8601 格式
        })));
      }

      // 處理隔離措施資料
      if (existingWorkPermit.isolationOptions) {
        setIsolationOptions(existingWorkPermit.isolationOptions.map((option: any) => ({
          ...option,
          date: option.date ? new Date(option.date).toISOString().slice(0, 16) : '' // 轉換為 datetime-local 格式
        })));
      }

      // 處理通風控制措施資料
      if (existingWorkPermit.ventOptions) {
        setVentOptions(existingWorkPermit.ventOptions.map((option: any) => ({
          ...option,
          date: option.date ? new Date(option.date).toISOString().slice(0, 16) : '' // 轉換為 datetime-local 格式
        })));
      }

      if(existingWorkPermit.emergencyEquipments) {
        setEmergencyEquipments(existingWorkPermit.emergencyEquipments.map((equipment: any) => ({
          ...equipment,
          qty: equipment.qty || ''
        })));
      }

      if(existingWorkPermit.protectiveEquipments) {
        setProtectiveEquipments(existingWorkPermit.protectiveEquipments.map((equipment: any) => ({
          ...equipment,
          qty: equipment.qty || ''
        })));
      }

      if(existingWorkPermit.safetyMeasures) {
        setSafetyMeasures(existingWorkPermit.safetyMeasures.map((measure: any) => ({
          ...measure,
          checked: measure.checked || false
        })));
      }

      setGroundPipeOptions(existingWorkPermit.groundPipeOptions);
    }
  }, [mode, formData, existingWorkPermit]);

  // 工人
  const [workers, setWorkers] = useState<Worker[]>([
    { name: '', idNumber: '', idExpiry: '', sign: '', signUrl: '' },
  ]);
  const [standbyWorkers, setStandbyWorkers] = useState<StandbyWorker[]>([
    { name: '', trainingDate: '', duty: defaultStandbyDuty, sign: '', signUrl: '' },
  ]);
  const [rescueWorkers, setRescueWorkers] = useState<RescueWorker[]>([
    { name: '', trainingDate: '', duty: defaultRescueDuty, sign: '', signUrl: '' },
  ]);

  // 空氣測試
  const [airTests, setAirTests] = useState<AirTest[]>([
    {
      date: '',
      deviceModel: '',
      deviceSerial: '',
      deviceValid: '',
      locations: [
        { place: '', time: '', result: { oxygen: '', lowerLimit: '', h2s: '', co: '' } },
      ],
      responsibleName: '',
      sign: '',
      signUrl: '',
    },
  ]);

  // 地底喉管工作
  const [groundPipeOptions, setGroundPipeOptions] = useState({
    isSubjectToRegulation: false,         // 是否屬於法例第9(b)條
    requireRespiratoryEquipment: false,   // 需要配戴呼吸器具及安全帶
    requireGasMonitoring: false,          // 需要連續氣體監測
    isNotSubjectToRegulation: false,      // 不屬於法例第9(b)條
    notSubjectReason: '',
  });
  // 隔離措施
  const [isolationOptions, setIsolationOptions] = useState([
    { label: '在密閉空間內日常運作已經停止', checked: false, sign: '', signUrl: '', date: '' },
    { label: '已隔離所有有必要的動力來源(電力/機械/氣動/液壓/其他)', checked: false, sign: '', signUrl: '', date: '' },
    { label: '已完全地關閉或移去明所有連接密閉空間的喉管', checked: false, sign: '', signUrl: '', date: '' },
    { label: '已將密閉空間與危害性氣體來源的供應喉管末端', checked: false, sign: '', signUrl: '', date: '' },
    { label: '已隔離其他有危險的來源(請列出___________)', checked: false, sign: '', signUrl: '', date: '' },
    { label: '已將密閉空間的蓋上反加上標簽，以防止未經許可的或意外的開啟', checked: false, sign: '', signUrl: '', date: '' },
  ]);
  // 清洗及通風控制措施
  const [ventOptions, setVentOptions] = useState([
    { label: '已密閉空間進行妥善的清洗(方法:___________)', checked: false, sign: '', signUrl: '', date: '' },
    { label: '已清除所有在密閉空間內的危害性物質', checked: false, sign: '', signUrl: '', date: '' },
    { label: '已提供妥善的中性呼吸的氣氛及有效的強制通風', checked: false, sign: '', signUrl: '', date: '' },
  ]);

  // 進入密閉空間所需的安全預防措施
  const [safetyMeasures, setSafetyMeasures] = useState([
    { label: '在該密閉空間內可造成危險的每項機械設備已被斷電， 而其電源電掣已上鎖，警告告示及被撤除；', checked: false },
    { label: '在該密閉空間內有可造成危險的每件財物內含的物質已被移除，警告狀態管已妥為封閉；', checked: false },
    { label: '已對該密閉空間進行空氣測試以確保沒有任何具危害性的氣體存在以及並無空氣氧氣情況；', checked: false },
    { label: '經風扇送氣後的情況下，該密閉空間但有引起的氣體狀況以及充分的換氣和通風，以確保該密閉空間是一個安全的工作場所；', checked: false },
    { label: '已在該密閉空間內提供妥善的中性呼吸的空氣及有效的強制通風；', checked: false },
    { label: '已採取有效的步驟以防止一(具危害性的氣體、蒸氣、煙霧或塵埃進入該密閉空間)及(ii)日常啟動的機械設備；', checked: false },
    { label: '已制訂適當的緊急程序，以應對密閉空間內可危及工人的任何嚴重和迫切的危險，包括提供足夠而狀況合適的緊急救護裝備(並須保持該等緊急救護裝備可供取用)：\n 認可呼吸器具； \n 使失去知覺的工人復甦的適當器具； \n 貯存氣氧或空氣的容器； \n 安全帶和救護繩； ', checked: false },
    { label: '密閉空間內的工人能向身在密閉空間外的人不當的聽覺與視覺警報裝置；', checked: false },
    { label: '已確保有充足數量並已妥為訓練的人員組成之緊急救援隊，並確保備妥結構在意外發生時負責實施緊急程序。所有緊急救援隊員亦已接受定期在有適當、有關緊急程序訓練。其訓練包括緊急救護訓練和急救訓練及如何正確操作該救護裝備的所有細節；', checked: false },
    { label: '已向所有在密閉空間內工作或接觸密閉空間的外圍地點進行工作的工人，提供有關密閉空間內工作的所有工人安全及處置應急程序的書面指引，通知易、易於查閱的地方，並確保所有有關工人均已閱讀該等書面指引，並且在密閉空間內工作的時候均有該等書面指引的副本；', checked: false },
    { label: '已提供一切可需於協助從密閉空間的工人的安全及健康，包括在有需要時提供合適的空氣監測設備(須為防爆設計)作連續性的監測密閉空間；', checked: false },
    { label: '已確保只有核准工人才可進入密閉空間或其式其內工作；', checked: false },
    { label: '已委派一名「候命人」在密閉空間外，以便可與密閉空間內的工人保持聯絡；', checked: false },
    { label: '已清楚知會密閉空間內的相關人士應於有危險情況時立即工作並離開；', checked: false },
    { label: '當有工人仍逗留在密閉空間時，須確保上述的安全預防措施持續有效；', checked: false },
    { label: '已安排在整個工作期間於密閉空間的出入口抽通換氣， 以監督相關人員已達致安全預防措施。', checked: false },
  ]);

  // 已提供的緊急救援器具
  const [emergencyEquipments, setEmergencyEquipments] = useState([
    { label: '認可呼吸器具', checked: false, qty: '' },
    { label: '使失去知覺的工人復甦的適當器具', checked: false, qty: '' },
    { label: '貯存氣氧或空氣的容器', checked: false, qty: '' },
    { label: '安全帶和救護繩', checked: false, qty: '' },
    { label: '使密閉空間內的工人能向身在密閉空間外的人不當的聽覺與視覺警報裝置', checked: false, qty: '' },
    { label: '其他相關緊急救援裝備', checked: false, desc: '', car: false, tripod: false, other: '', qty: '' },
  ]);
  const [emergencyConfirm, setEmergencyConfirm] = useState(false);

  // 已提供的防護設備清單
  const [protectiveEquipments, setProtectiveEquipments] = useState([
    { label: '強制通風設備', checked: false, qty: '' },
    { label: '連繫性的氣壓警測訓練', checked: false, qty: '' },
    { label: '對講機(防爆設計)', checked: false, qty: '' },
    { label: '防護屏障', checked: false, qty: '' },
    { label: '照明燈具', checked: false, qty: '' },
    { label: '其他(請列出)', checked: false, desc: '', qty: '' },
    // 個人防護設備
    { label: '認可呼吸器具(不包括供緊急用途)', checked: false, qty: '' },
    { label: '聽覺防護裝置', checked: false, qty: '' },
    { label: '防護衣物', checked: false, qty: '' },
    { label: '頭、手、腳部防護', checked: false, qty: '' },
    { label: '救生繩及帶', checked: false, qty: '' },
    { label: '眼部保護', checked: false, qty: '' },
    { label: '其他(請列出)', checked: false, desc: '', qty: '' },
  ]);

  // 簽名板 ref
  const workerPadRefs = useRef<any[]>([]);
  const standbyPadRefs = useRef<any[]>([]);
  const rescuePadRefs = useRef<any[]>([]);
  const airTestPadRefs = useRef<any[]>([]);
  const declarationPadRefs = useRef<any[]>([]);
  const isolationPadRefs = useRef<any[]>([]);
  const ventPadRefs = useRef<any[]>([]);

  // 工人表格操作
  const handleWorkerChange = (idx: number, key: keyof Worker, value: string) => {
    const newWorkers = [...workers];
    newWorkers[idx][key] = value;
    setWorkers(newWorkers);
  };
  const handleAddWorker = () => setWorkers([...workers, { name: '', idNumber: '', idExpiry: '', sign: '' }]);
  const handleRemoveWorker = (idx: number) => setWorkers(workers.filter((_, i) => i !== idx));
  const handleWorkerSign = (idx: number) => {
    const signaturePad = workerPadRefs.current[idx];
    if (signaturePad && signaturePad.getTrimmedCanvas) {
      const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
      const base64String = (dataUrl);
      handleWorkerChange(idx, 'sign', base64String);
    }
  };
  const handleWorkerClear = (idx: number) => {
    const signaturePad = workerPadRefs.current[idx];
    if (signaturePad && signaturePad.clear) {
      signaturePad.clear();
      handleWorkerChange(idx, 'sign', '');
    }
  };

  // 候命人員表格操作
  const handleStandbyChange = (idx: number, key: keyof StandbyWorker, value: string) => {
    const newStandby = [...standbyWorkers];
    newStandby[idx][key] = value;
    setStandbyWorkers(newStandby);
  };
  const handleAddStandby = () => setStandbyWorkers([...standbyWorkers, { name: '', trainingDate: '', duty: defaultStandbyDuty, sign: '', signUrl: '' }]);
  const handleRemoveStandby = (idx: number) => setStandbyWorkers(standbyWorkers.filter((_, i) => i !== idx));
  const handleStandbySign = (idx: number) => {
    const signaturePad = standbyPadRefs.current[idx];
    if (signaturePad && signaturePad.getTrimmedCanvas) {
      const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
      const base64String = (dataUrl);
      handleStandbyChange(idx, 'sign', base64String);
    }
  };
  const handleStandbyClear = (idx: number) => {
    const signaturePad = standbyPadRefs.current[idx];
    if (signaturePad && signaturePad.clear) {
      signaturePad.clear();
      handleStandbyChange(idx, 'sign', '');
    }
  };

  // 救援人員表格操作
  const handleRescueChange = (idx: number, key: keyof RescueWorker, value: string) => {
    const newRescue = [...rescueWorkers];
    newRescue[idx][key] = value;
    setRescueWorkers(newRescue);
  };
  const handleAddRescue = () => setRescueWorkers([...rescueWorkers, { name: '', trainingDate: '', duty: defaultRescueDuty, sign: '', signUrl: '' }]);
  const handleRemoveRescue = (idx: number) => setRescueWorkers(rescueWorkers.filter((_, i) => i !== idx));
  const handleRescueSign = (idx: number) => {
    const signaturePad = rescuePadRefs.current[idx];
    if (signaturePad && signaturePad.getTrimmedCanvas) {
      const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
      const base64String = (dataUrl);
      handleRescueChange(idx, 'sign', base64String);
    }
  };
  const handleRescueClear = (idx: number) => {
    const signaturePad = rescuePadRefs.current[idx];
    if (signaturePad && signaturePad.clear) {
      signaturePad.clear();
      handleRescueChange(idx, 'sign', '');
    }
  };

  const handleAirTestChange = (idx: number, key: keyof AirTest, value: any) => {
    const arr = [...airTests];
    arr[idx] = { ...arr[idx] };
    arr[idx][key] = value;
    setAirTests(arr);
  };
  const handleAirTestLocationChange = (testIdx: number, locIdx: number, key: keyof AirTest['locations'][0], value: any) => {
    const arr = [...airTests];
    arr[testIdx] = { ...arr[testIdx] };
    arr[testIdx].locations = [...arr[testIdx].locations];
    arr[testIdx].locations[locIdx] = { ...arr[testIdx].locations[locIdx] };
    arr[testIdx].locations[locIdx][key] = value;
    setAirTests(arr);
  };
  const handleAirTestResultChange = (testIdx: number, locIdx: number, key: keyof AirTest['locations'][0]['result'], value: any) => {
    const arr = [...airTests];
    arr[testIdx] = { ...arr[testIdx] };
    arr[testIdx].locations = [...arr[testIdx].locations];
    arr[testIdx].locations[locIdx] = { ...arr[testIdx].locations[locIdx] };
    arr[testIdx].locations[locIdx].result = { ...arr[testIdx].locations[locIdx].result };
    arr[testIdx].locations[locIdx].result[key] = value;
    setAirTests(arr);
  };

  // 添加新的測試位置
  const handleAddAirTestLocation = (testIdx: number) => {
    const arr = [...airTests];
    arr[testIdx] = { ...arr[testIdx] };
    arr[testIdx].locations = [...arr[testIdx].locations];
    arr[testIdx].locations.push({
      place: '',
      time: '',
      result: { oxygen: '', lowerLimit: '', h2s: '', co: '' }
    });
    setAirTests(arr);
  };

  // 移除測試位置
  const handleRemoveAirTestLocation = (testIdx: number, locIdx: number) => {
    const arr = [...airTests];
    arr[testIdx] = { ...arr[testIdx] };
    arr[testIdx].locations = [...arr[testIdx].locations];
    arr[testIdx].locations.splice(locIdx, 1);
    setAirTests(arr);
  };

  // 儲存
  const handleSave = async () => {
    // 在查看模式下不執行保存
    if (isViewMode) {
      return;
    }

    // 組裝所有欄位，將日期轉換為 ISO 8601 格式
    const data = {
      scheduleID, // 添加 scheduleID
      mode, // 添加模式信息
      site,
      content,
      contractor,
      approver,
      riskReportDate: riskReportDate ? new Date(riskReportDate).toISOString() : '',
      entryDate,
      entryTime,
      expiryDate,
      expiryTime,
      maxStayHour: Number(maxStayHour),
      workers: workers.map(worker => ({
        ...worker,
        idExpiry: worker.idExpiry ? new Date(worker.idExpiry).toISOString().split('T')[0] : ''
      })),
      standbyWorkers: standbyWorkers.map(worker => ({
        ...worker,
        trainingDate: worker.trainingDate ? new Date(worker.trainingDate).toISOString().split('T')[0] : ''
      })),
      rescueWorkers: rescueWorkers.map(worker => ({
        ...worker,
        trainingDate: worker.trainingDate ? new Date(worker.trainingDate).toISOString().split('T')[0] : ''
      })),
      groundPipeOptions,
      isolationOptions: isolationOptions.map(option => ({
        ...option,
        date: option.date ? new Date(option.date).toISOString() : ''
      })),
      ventOptions: ventOptions.map(option => ({
        ...option,
        date: option.date ? new Date(option.date).toISOString() : ''
      })),
      airTests: airTests.map(test => ({
        ...test,
        date: test.date ? new Date(test.date).toISOString().split('T')[0] : '',
        deviceValid: test.deviceValid ? new Date(test.deviceValid).toISOString().split('T')[0] : '',
        locations: test.locations.map(location => ({
          ...location,
          time: location.time || '' // 直接使用時間字符串格式 "14:30"
        }))
      })),
      safetyMeasures,
      emergencyEquipments,
      emergencyConfirm,
      protectiveEquipments,
      declarations: declarations.map(declaration => ({
        ...declaration,
        validDate: declaration.validDate ? new Date(declaration.validDate).toISOString().split('T')[0] : '',
        validTime: declaration.validTime || '', // 直接使用時間字符串格式 "14:30"
        date: declaration.date ? new Date(declaration.date).toISOString() : ''
      }))
    };

    //console.log('保存數據:', data);

    // 送出到後端
    try {

      // 根據模式選擇使用更新或創建
      const mutation = mode === 'create' ? createWorkPermit : createWorkPermit;

      try {
        const result = await mutation(data).unwrap();
        //console.log('保存成功:', result);
        alert('送出成功！');
        navigate('/add-work-schedule', {
          state: {
            project: project,
            position: position,
            entry: entry
          }
        });
      } catch (error) {
        console.error('保存失敗:', error);
        alert('送出失敗，請稍後再試');
      }
    } catch (error) {
      console.error('保存錯誤:', error);
      alert('網路錯誤，請稍後再試');
    }
  };

  const handleClose = () => {
    !isViewMode ? navigate('/add-work-schedule', {
      state: {
        project: project,
        position: position,
        entry: entry
      }
    }) : navigate('/entry-detail', {
      state: {
        project: project,
        position: position,
        entry: entry
      }
    });
  };

  // 下載工作許可證報告
  const handleDownloadReport = async () => {
    if (!scheduleID) {
      alert('無法下載：缺少工作排程 ID');
      return;
    }

    try {
      const blob = await getWorkPermitReport(scheduleID).unwrap();

      // 創建下載連結
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // 設置檔案名稱
      const fileName = `WorkPermit_${scheduleID}_${dayjs().format('YYYY-MM-DD')}.pdf`;
      link.download = fileName;

      // 觸發下載
      document.body.appendChild(link);
      link.click();

      // 清理
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下載工作許可證報告失敗:', error);
      alert('下載失敗，請稍後再試');
    }
  };

  const handleIsolationSignEnd = (idx: number) => {
    setIsolationOptions(prev =>
      prev.map((item, i) =>
        i === idx
          ? { ...item, date: new Date().toISOString().slice(0, 16), checked: true } // 轉換為 datetime-local 格式
          : item
      )
    );
  };

  const handleVentOptionsSignEnd = (idx: number) => {
    setVentOptions(prev =>
      prev.map((item, i) =>
        i === idx
          ? { ...item, date: new Date().toISOString().slice(0, 16), checked: true } // 轉換為 datetime-local 格式
          : item
      )
    );
  };

  // 聲明區塊
  enum Stakeholder {
    Owner = 'Owner',
    Contractor = 'Contractor',
    AuthorizedRepresentative = 'AuthorizedRepresentative',
  }

  const [declarations, setDeclarations] = useState([
    {
      title: 'WorkPermitCertificate',
      desc: '本人是上述密閉空間工程的東主/承建商/其授權代表，確認已核查上述合資格人士的危險評估報告並遵守《工廠及工業經營(密閉空間)規例》第5(2)條所提述的所有事項；並就所有需要的安排均已落實，並就此發出本許可工作證明書。',
      stakeholder: '',
      validDate: '',
      validTime: '',
      sign: '',
      signUrl: '',
      name: '',
      position: '',
      date: '',
    },
    {
      title: 'ApprovedWorkPermitCertificate',
      desc: '本人已閱讀及明白此工作許可證的有關規定和工作許可證內指示而工作。',
      sign: '',
      signUrl: '',
      name: '',
      position: '',
      date: '',
    },
    {
      title: 'CompletionCertificate',
      desc: '本人確認上述密閉空間工作已經完成，所有被指派人士、物料和設備已搬離現場，並已證實所有人士該密閉空間已不可以安全進入，就此簽署確認。',
      sign: '',
      signUrl: '',
      name: '',
      position: '',
      date: '',
    },
    {
      title: 'CancelledWorkPermitCertificate',
      desc: '本人是上述密閉空間工程的東主/承建商/其授權代表，現聲明確認註銷是次工作許可證明書。本人明白如要繼續進行工作，須重新簽發工作許可證明書。',
      stakeholder: '',
      sign: '',
      signUrl: '',
      name: '',
      position: '',
      date: '',
    },
  ]);

  const handleDeclarationSignEnd = (idx: number) => {
    setDeclarations(prev =>
      prev.map((item, i) =>
        i === idx
          ? { ...item, date: new Date().toISOString().slice(0, 16) } // 轉換為 datetime-local 格式
          : item
      )
    );
  };



  // 顯示載入狀態
  if (isLoadingWorkPermit) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">{t('Loading Work Permit Data...')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={handleClose}
            sx={{
              mr: 2,
              backgroundColor: '#f4a460',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#e69450',
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>
        <Typography variant="h5" sx={{ textAlign: 'center', flex: 1 }}>
          {mode === 'create' ? t('Create Work Permit') : mode === 'edit' ? t('Edit Work Permit') : t('View Work Permit')}
          {/* {scheduleID && ` (ID: ${scheduleID})`} */}
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {isViewMode && (
            <Button
              variant="contained"
              onClick={handleDownloadReport}
              startIcon={<DownloadIcon />}
              sx={{
                backgroundColor: '#f4a460',
                color: '#fff',
                fontWeight: 'bold',
                borderRadius: '8px',
                padding: '8px 16px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: '#e69450',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {t('Download PDF')}
            </Button>
          )}
        </Box>
      </Box>


      {/* 基本資料區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField label={t('Work Site')} value={site} onChange={e => setSite(e.target.value)} fullWidth disabled={isViewMode} /></Grid>
          <Grid item xs={12} sm={6}><TextField label={t('Work Content')} value={content} onChange={e => setContent(e.target.value)} fullWidth disabled={isViewMode} /></Grid>
          <Grid item xs={12} sm={6}><TextField label={t('Contractor/Owner')} value={contractor} onChange={e => setContractor(e.target.value)} fullWidth disabled={isViewMode} /></Grid>
          <Grid item xs={12} sm={6}><TextField label={t('Approved Qualified Person')} value={approver} onChange={e => setApprover(e.target.value)} fullWidth disabled={isViewMode} /></Grid>
          <Grid item xs={12} sm={6}><TextField label={t('Risk Assessment Report Date and Time')} type="datetime-local" value={riskReportDate} onChange={e => setRiskReportDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} disabled={isViewMode} /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField label={t('Entry Date')} type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} disabled={isViewMode} />
            <TextField label={t('Time')} type="time" value={entryTime} onChange={e => setEntryTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} disabled={isViewMode} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label={t('Validity Period Until (Date)')} type="date" value={expiryDate} onChange={e => setExpiryDate(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} disabled={isViewMode} />
            <TextField label={t('Time')} type="time" value={expiryTime} onChange={e => setExpiryTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} disabled={isViewMode} />
          </Grid>
        </Grid>
      </Paper>
      {/* 工作人員區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Approved Workers')}</Typography>
        <TextField label={t('Maximum Stay Time for Approved Workers (Hours)')} type="number" value={maxStayHour} onChange={e => setMaxStayHour(e.target.value)} fullWidth sx={{ my: 2 }} disabled={isViewMode} />
        {!isViewMode && <Button onClick={handleAddWorker} sx={{ mb: 1 }}>{t('Add Worker')}</Button>}
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ background: '#eee' }}>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Name')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('ID Number')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('ID Expiry Date')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Worker Signature')}</th>
                {!isViewMode && <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {workers.map((worker, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}><TextField value={worker.name} onChange={e => handleWorkerChange(idx, 'name', e.target.value)} size="small" disabled={isViewMode} /></td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}><TextField value={worker.idNumber} onChange={e => handleWorkerChange(idx, 'idNumber', e.target.value)} size="small" disabled={isViewMode} /></td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}><TextField type="date" value={worker.idExpiry} onChange={e => handleWorkerChange(idx, 'idExpiry', e.target.value)} size="small" InputLabelProps={{ shrink: true }} disabled={isViewMode} /></td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    {worker.signUrl ?
                      <img src={worker.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                      :
                      !isViewMode && (
                        <>
                          <SignaturePad ref={el => workerPadRefs.current[idx] = el} canvasProps={{ width: 150, height: 40, style: { border: '1px solid #000' } }} />
                          <Box>
                            <Button size="small" onClick={() => handleWorkerSign(idx)}>{t('Save')}</Button>
                            <Button size="small" onClick={() => handleWorkerClear(idx)}>{t('Clear')}</Button>
                          </Box>
                        </>
                      )
                    }
                    {worker.sign && <img src={base64ToDataURL(worker.sign)} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />}
                  </td>
                  {!isViewMode && !worker.signUrl && (
                    <td style={{ border: '1px solid #ccc', padding: 4 }}>
                      <IconButton onClick={() => handleRemoveWorker(idx)} disabled={workers.length === 1}>{t('Delete')}</IconButton>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Standby Workers')}</Typography>
        {!isViewMode && <Button onClick={handleAddStandby} sx={{ mb: 1 }}>{t('Add Standby Worker')}</Button>}
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ background: '#eee' }}>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Name')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Training Date')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Duty')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Worker Signature')}</th>
                {!isViewMode && <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {standbyWorkers.map((standby, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}><TextField value={standby.name} onChange={e => handleStandbyChange(idx, 'name', e.target.value)} size="small" disabled={isViewMode} /></td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}><TextField type="date" value={standby.trainingDate} onChange={e => handleStandbyChange(idx, 'trainingDate', e.target.value)} size="small" InputLabelProps={{ shrink: true }} disabled={isViewMode} /></td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    <Typography sx={{ whiteSpace: 'pre-line' }}>{standby.duty}</Typography></td>
                    {/* <TextField disabled value={standby.duty} onChange={e => handleStandbyChange(idx, 'duty', e.target.value)} size="small" multiline minRows={2} /></td> */}
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>

                    {standby.signUrl ?
                      <img src={standby.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                      :
                      !isViewMode && (
                        <>
                          <SignaturePad ref={el => standbyPadRefs.current[idx] = el} canvasProps={{ width: 150, height: 40, style: { border: '1px solid #000' } }} />
                          <Box>
                            <Button size="small" onClick={() => handleStandbySign(idx)}>{t('Save')}</Button>
                            <Button size="small" onClick={() => handleStandbyClear(idx)}>{t('Clear')}</Button>
                          </Box>
                        </>
                      )
                    }
                    {standby.sign && <img src={base64ToDataURL(standby.sign)} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />}
                  </td>
                  {!isViewMode && !standby.signUrl && (
                    <td style={{ border: '1px solid #ccc', padding: 4 }}>
                      <IconButton onClick={() => handleRemoveStandby(idx)} disabled={standbyWorkers.length === 1}>{t('Delete')}</IconButton>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Rescue Workers on Site')}</Typography>
        {!isViewMode && <Button onClick={handleAddRescue} sx={{ mb: 1 }}>{t('Add Rescue Worker')}</Button>}
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ background: '#eee' }}>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Name')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Emergency Rescue Training Date')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Duty')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Worker Signature')}</th>
                {!isViewMode && <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Actions')}</th>}
              </tr>
            </thead>
            <tbody>
              {rescueWorkers.map((rescue, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}><TextField value={rescue.name} onChange={e => handleRescueChange(idx, 'name', e.target.value)} size="small" disabled={isViewMode} /></td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}><TextField type="date" value={rescue.trainingDate} onChange={e => handleRescueChange(idx, 'trainingDate', e.target.value)} size="small" InputLabelProps={{ shrink: true }} disabled={isViewMode} /></td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    <Typography sx={{ whiteSpace: 'pre-line' }}>{rescue.duty}</Typography></td>
                    {/* <TextField disabled value={rescue.duty} onChange={e => handleRescueChange(idx, 'duty', e.target.value)} size="small" multiline minRows={2} /></td> */}
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    {rescue.signUrl ?
                      <img src={rescue.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                      :
                      !isViewMode && (
                        <>
                          <SignaturePad ref={el => rescuePadRefs.current[idx] = el} canvasProps={{ width: 150, height: 40, style: { border: '1px solid #000' } }} />
                          <Box>
                            <Button size="small" onClick={() => handleRescueSign(idx)}>{t('Save')}</Button>
                            <Button size="small" onClick={() => handleRescueClear(idx)}>{t('Clear')}</Button>
                          </Box>
                        </>
                      )
                    }
                    {rescue.sign && <img src={base64ToDataURL(rescue.sign)} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />}
                  </td>
                  {!isViewMode && !rescue.signUrl && (
                    <td style={{ border: '1px solid #ccc', padding: 4 }}>
                      <IconButton onClick={() => handleRemoveRescue(idx)} disabled={rescueWorkers.length === 1}>{t('Delete')}</IconButton>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
      {/* 地底喉管工作區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Ground Pipe Work')}</Typography>
        <Box sx={{ mb: 1 }}>
          <Checkbox
            checked={groundPipeOptions.isSubjectToRegulation}
            onChange={e => setGroundPipeOptions({ ...groundPipeOptions, isSubjectToRegulation: e.target.checked })}
            disabled={isViewMode}
          />
          {t('This confined space work is subject to Section 9(b) of the Factory and Industrial Operations (Confined Spaces) Regulations, which refers to ground pipe work. Therefore, the contractor/owner must')}
        </Box>
        <Box sx={{ ml: 3, mb: 1 }}>
          <Checkbox
            checked={groundPipeOptions.requireRespiratoryEquipment}
            onChange={e => setGroundPipeOptions({ ...groundPipeOptions, requireRespiratoryEquipment: e.target.checked })}
            disabled={isViewMode}
          />
          {t('ensure that any person entering the confined space or remaining in it is properly equipped with appropriate respiratory equipment; and')}
          <br />
          (i) {t('wear appropriate approved breathing apparatus; and')}
          <br />
          (ii) {t('wear a safety harness connected to a lifeline')}
        </Box>
        <Box sx={{ ml: 3, mb: 1 }}>
          <Checkbox
            checked={groundPipeOptions.requireGasMonitoring}
            onChange={e => setGroundPipeOptions({ ...groundPipeOptions, requireGasMonitoring: e.target.checked })}
            disabled={isViewMode}
          />
          {t('use appropriate monitoring equipment (designed for explosion-proof) to continuously monitor the gas in the confined space until all persons have left the confined space; and')}
        </Box>
        <Box sx={{ mb: 1 }}>
          <Checkbox
            checked={groundPipeOptions.isNotSubjectToRegulation}
            onChange={e => setGroundPipeOptions({ ...groundPipeOptions, isNotSubjectToRegulation: e.target.checked })}
            disabled={isViewMode}
          />
          {t('This confined space work is not subject to Section 9(b) of the Factory and Industrial Operations (Confined Spaces) Regulations, for the following reasons:')}
        </Box>
        <TextField label={t('Reason')} value={groundPipeOptions.notSubjectReason} onChange={e => setGroundPipeOptions({ ...groundPipeOptions, notSubjectReason: e.target.value })} fullWidth sx={{ mb: 1 }} disabled={isViewMode} />
        <Typography variant="body2" color="text.secondary">{t('Note: The above options must be mutually exclusive.')}</Typography>
      </Paper>
      {/* 隔離措施區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Isolation Measures')}</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ background: '#eee' }}>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Measure')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Signature')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Date and Time')}</th>
              </tr>
            </thead>
            <tbody>
              {isolationOptions.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    <Checkbox checked={item.checked} disabled={isViewMode} onChange={e => {
                      const arr = [...isolationOptions];
                      arr[idx].checked = e.target.checked;
                      setIsolationOptions(arr);
                    }} />
                    {item.label}
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    {item.signUrl ? <img src={item.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                      :
                      !isViewMode && (
                        <>
                          <SignaturePad ref={el => isolationPadRefs.current[idx] = el} canvasProps={{ width: 150, height: 40, style: { border: '1px solid #000' } }} onEnd={() => handleIsolationSignEnd(idx)} />
                          <Box>
                            <Button size="small" onClick={() => {
                              const signaturePad = isolationPadRefs.current[idx];
                              if (signaturePad && signaturePad.getTrimmedCanvas) {
                                const arr = [...isolationOptions];
                                const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
                                const base64String = (dataUrl);
                                arr[idx].sign = base64String;
                                setIsolationOptions(arr);
                              }
                            }}>{t('Save')}</Button>
                            <Button size="small" onClick={() => {
                              const signaturePad = isolationPadRefs.current[idx];
                              if (signaturePad && signaturePad.clear) {
                                signaturePad.clear();
                                const arr = [...isolationOptions];
                                arr[idx].sign = '';
                                setIsolationOptions(arr);
                              }
                            }}>{t('Clear')}</Button>
                          </Box>
                        </>
                      )
                    }
                    {item.sign && <img src={base64ToDataURL(item.sign)} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />}
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    <TextField disabled={isViewMode} type="datetime-local" value={item.date} onChange={e => {
                      const arr = [...isolationOptions];
                      arr[idx].date = e.target.value;
                      setIsolationOptions(arr);
                    }} size="small" InputLabelProps={{ shrink: true }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
      {/* 清洗及通風控制措施區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Cleaning and Ventilation Control Measures')}</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr style={{ background: '#eee' }}>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Measure')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Signature')}</th>
                <th style={{ border: '1px solid #ccc', padding: 4 }}>{t('Date and Time')}</th>
              </tr>
            </thead>
            <tbody>
              {ventOptions.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    <Checkbox disabled={isViewMode} checked={item.checked} onChange={e => {
                      const arr = [...ventOptions];
                      arr[idx].checked = e.target.checked;
                      setVentOptions(arr);
                    }} />
                    {item.label}
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    {item.signUrl ? <img src={item.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                      :
                      !isViewMode && (
                      <>
                        <SignaturePad ref={el => ventPadRefs.current[idx] = el} canvasProps={{ width: 150, height: 40, style: { border: '1px solid #000' } }} onEnd={() => handleVentOptionsSignEnd(idx)} />
                        <Box>
                          <Button size="small" onClick={() => {
                            const signaturePad = ventPadRefs.current[idx];
                            if (signaturePad && signaturePad.getTrimmedCanvas) {
                              const arr = [...ventOptions];
                              const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
                              const base64String = (dataUrl);
                              arr[idx].sign = base64String;
                              setVentOptions(arr);
                            }
                          }}>{t('Save')}</Button>
                          <Button size="small" onClick={() => {
                            const signaturePad = ventPadRefs.current[idx];
                            if (signaturePad && signaturePad.clear) {
                              signaturePad.clear();
                              const arr = [...ventOptions];
                              arr[idx].sign = '';
                              setVentOptions(arr);
                            }
                          }}>{t('Clear')}</Button>
                        </Box>
                      </>)
                    }
                    {item.sign && <img src={base64ToDataURL(item.sign)} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />}
                  </td>
                  <td style={{ border: '1px solid #ccc', padding: 4 }}>
                    <TextField disabled={isViewMode} type="datetime-local" value={item.date} onChange={e => {
                      const arr = [...ventOptions];
                      arr[idx].date = e.target.value;
                      setVentOptions(arr);
                    }} size="small" InputLabelProps={{ shrink: true }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
      {/* 空氣測試區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Air Testing')}</Typography>
        {/* {!isViewMode && <Button onClick={handleAddAirTest} sx={{ mb: 1 }}>{t('Add Air Test')}</Button>} */}
        {airTests.map((test, idx) => (
          <Box key={idx} sx={{ border: '1px solid #ccc', borderRadius: 2, mb: 2, p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}><TextField label={t('Test Date')} type="date" value={test.date} onChange={e => handleAirTestChange(idx, 'date', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} disabled={isViewMode} /></Grid>
              <Grid item xs={12} sm={3}><TextField label={t('Air Monitoring Equipment Model')} value={test.deviceModel} onChange={e => handleAirTestChange(idx, 'deviceModel', e.target.value)} fullWidth disabled={isViewMode} /></Grid>
              <Grid item xs={12} sm={3}><TextField label={t('Air Monitoring Equipment Serial Number')} value={test.deviceSerial} onChange={e => handleAirTestChange(idx, 'deviceSerial', e.target.value)} fullWidth disabled={isViewMode} /></Grid>
              <Grid item xs={12} sm={3}><TextField label={t('Air Monitoring Equipment Calibration Validity Until')} type="date" value={test.deviceValid} onChange={e => handleAirTestChange(idx, 'deviceValid', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} disabled={isViewMode} /></Grid>
            </Grid>
            {test.locations.map((loc, locIdx) => (
              <Box key={locIdx} sx={{ mt: 2, border: '1px solid #eee', borderRadius: 1, p: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2">{t('Test Location')} #{locIdx + 1}</Typography>
                  {!isViewMode && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveAirTestLocation(idx, locIdx)}
                      disabled={test.locations.length === 1}
                    >
                      {t('Remove Location')}
                    </Button>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={3}><TextField label={t('Test Location')} value={loc.place} onChange={e => handleAirTestLocationChange(idx, locIdx, 'place', e.target.value)} fullWidth disabled={isViewMode} /></Grid>
                  <Grid item xs={12} sm={3}><TextField label={t('Test Time')} type="time" value={loc.time} onChange={e => handleAirTestLocationChange(idx, locIdx, 'time', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} disabled={isViewMode} /></Grid>
                  <Grid item xs={12} sm={6}>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}><TextField label={t('Oxygen')} type="number" value={loc.result.oxygen} onChange={e => handleAirTestResultChange(idx, locIdx, 'oxygen', e.target.value)} fullWidth size="small" disabled={isViewMode} /></Grid>
                      <Grid item xs={6} sm={3}><TextField label={t('Lower Limit (Percentage)')} type="number" value={loc.result.lowerLimit} onChange={e => handleAirTestResultChange(idx, locIdx, 'lowerLimit', e.target.value)} fullWidth size="small" disabled={isViewMode} /></Grid>
                      <Grid item xs={6} sm={3}><TextField label={t('Hydrogen Sulphide (ppm)')} type="number" value={loc.result.h2s} onChange={e => handleAirTestResultChange(idx, locIdx, 'h2s', e.target.value)} fullWidth size="small" disabled={isViewMode} /></Grid>
                      <Grid item xs={6} sm={3}><TextField label={t('Carbon Monoxide (ppm)')} type="number" value={loc.result.co} onChange={e => handleAirTestResultChange(idx, locIdx, 'co', e.target.value)} fullWidth size="small" disabled={isViewMode} /></Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            ))}
            {!isViewMode && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={() => handleAddAirTestLocation(idx)}
                  sx={{ mb: 1 }}
                >
                  {t('Add Test Location')}
                </Button>
              </Box>
            )}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} sm={6}><TextField label={t('Responsible Person for Air Testing')} value={test.responsibleName} onChange={e => handleAirTestChange(idx, 'responsibleName', e.target.value)} fullWidth disabled={isViewMode} /></Grid>
              <Grid item xs={12} sm={6}>
                <Typography>{t('Responsible Person Signature')}</Typography>
                {test.signUrl ? <img src={test.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                  :
                  !isViewMode && (
                    <>
                      <SignaturePad ref={el => airTestPadRefs.current[idx] = el} canvasProps={{ width: 150, height: 40, style: { border: '1px solid #000' } }} />
                      <Box>
                        <Button size="small" onClick={() => {
                          const signaturePad = airTestPadRefs.current[idx];
                          if (signaturePad && signaturePad.getTrimmedCanvas) {
                            const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
                            const base64String = (dataUrl);
                            handleAirTestChange(idx, 'sign', base64String);
                          }
                        }}>{t('Save')}</Button>
                        <Button size="small" onClick={() => {
                          const signaturePad = airTestPadRefs.current[idx];
                          if (signaturePad && signaturePad.clear) {
                            signaturePad.clear();
                            handleAirTestChange(idx, 'sign', '');
                          }
                        }}>{t('Clear')}</Button>
                      </Box>
                    </>
                  )
                }

                {test.sign && <img src={base64ToDataURL(test.sign)} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />}
              </Grid>
            </Grid>
          </Box>
        ))}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('□ I hereby confirm that I have tested the air in this confined space and found no hazardous gases and no oxygen deficiency.')}
        </Typography>
      </Paper>
      {/* 進入密閉空間所需的安全預防措施區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Safety Measures Required for Entry into Confined Spaces')}</Typography>
        <Box>
          {safetyMeasures.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 , marginTop:'10px'}}>
              <Checkbox disabled={isViewMode} checked={item.checked} onChange={e => {
                const arr = [...safetyMeasures];
                arr[idx].checked = e.target.checked;
                setSafetyMeasures(arr);
              }} />
              <Typography sx={{ whiteSpace: 'pre-line' }}>{item.label}</Typography>
            </Box>
          ))}
        </Box>
      </Paper>
      {/* 已提供的緊急救援器具區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Emergency Rescue Equipment Provided')}</Typography>
        <Box>
          {emergencyEquipments.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1, marginTop:'10px' }}>
              <Checkbox disabled={isViewMode} checked={item.checked} onChange={e => {
                const arr = [...emergencyEquipments];
                arr[idx].checked = e.target.checked;
                setEmergencyEquipments(arr);
              }} />
              <Typography sx={{ minWidth: 260 }}>{item.label}</Typography>
              {idx < 5 && (
                <TextField disabled={isViewMode} size="small" label={t('Quantity/Number')} value={item.qty} onChange={e => {
                  const arr = [...emergencyEquipments];
                  arr[idx].qty = e.target.value;
                  setEmergencyEquipments(arr);
                }} sx={{ width: 80, ml: 1 }} />
              )}
              {idx === 5 && item.checked && (
                <>
                  <TextField size="small" label={t('Description')} value={item.desc} onChange={e => {
                    const arr = [...emergencyEquipments];
                    arr[idx].desc = e.target.value;
                    setEmergencyEquipments(arr);
                  }} sx={{ width: 120, ml: 1 }} />
                  <Checkbox disabled={isViewMode} checked={item.car} onChange={e => {
                    const arr = [...emergencyEquipments];
                    arr[idx].car = e.target.checked;
                    setEmergencyEquipments(arr);
                  }} />{t('Tripod and Crane')}
                  <Checkbox disabled={isViewMode} checked={item.tripod} onChange={e => {
                    const arr = [...emergencyEquipments];
                    arr[idx].tripod = e.target.checked;
                    setEmergencyEquipments(arr);
                  }} />{t('Other')}
                  <TextField disabled={isViewMode} size="small" label="" value={item.other} onChange={e => {
                    const arr = [...emergencyEquipments];
                    arr[idx].other = e.target.value;
                    setEmergencyEquipments(arr);
                  }} sx={{ width: 80, ml: 1 }} />
                </>
              )}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
          <Checkbox disabled={isViewMode} checked={emergencyConfirm} onChange={e => setEmergencyConfirm(e.target.checked)} />
          <Typography>{t('I hereby confirm that the emergency rescue equipment provided is sufficient and in satisfactory condition, and is readily available at all times.')}</Typography>
        </Box>
      </Paper>
      {/* 已提供的防護設備清單區 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('List of Protective Equipment Provided')}</Typography>
        <Box>
          {protectiveEquipments.map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 1 ,marginTop:'10px'}}>
              <Checkbox disabled={isViewMode} checked={item.checked} onChange={e => {
                const arr = [...protectiveEquipments];
                arr[idx].checked = e.target.checked;
                setProtectiveEquipments(arr);
              }} />
              <Typography sx={{ minWidth: 260 }}>{item.label}</Typography>
              {'qty' in item && (
                <TextField disabled={isViewMode} size="small" label={item.label.includes(t('Clothing')) ? t('Piece') : t('Quantity/Number')} value={item.qty} onChange={e => {
                  const arr = [...protectiveEquipments];
                  arr[idx].qty = e.target.value;
                  setProtectiveEquipments(arr);
                }} sx={{ width: 80, ml: 1 }} />
              )}
              {'desc' in item && item.label.includes(t('Other')) && item.checked && (
                <TextField disabled={isViewMode} size="small" label={t('Description')} value={item.desc} onChange={e => {
                  const arr = [...protectiveEquipments];
                  arr[idx].desc = e.target.value;
                  setProtectiveEquipments(arr);
                }} sx={{ width: 120, ml: 1 }} />
              )}
            </Box>
          ))}
        </Box>
      </Paper>
      {/* 聲明區塊 */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ background: '#f4a460', p: 1, borderRadius: 1 }}>{t('Declarations by Owner/Contractor or Authorized Representative')}</Typography>
        {declarations.map((item, idx) => (
          <Box key={idx} sx={{ border: '1px solid #ccc', borderRadius: 2, mb: 2, p: 2 , marginTop:'10px'}}>
            <Typography sx={{ fontWeight: 'bold', mb: 1 }}>{t(item.title)}</Typography>
            <Typography sx={{ whiteSpace: 'pre-line', mb: 1 }}>{item.desc}</Typography>
            {/* 利害關係人選擇 - 只在第一個和第四個聲明顯示 */}
            {(idx === 0 || idx === 3) && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('Stakeholder Type')}:</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      disabled={isViewMode}
                      checked={item.stakeholder === Stakeholder.Owner}
                      onChange={e => {
                        const arr = [...declarations];
                        arr[idx].stakeholder = e.target.checked ? Stakeholder.Owner : '';
                        setDeclarations(arr);
                      }}
                    />
                    <Typography>{t('Owner')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                    disabled={isViewMode}
                      checked={item.stakeholder === Stakeholder.Contractor}
                      onChange={e => {
                        const arr = [...declarations];
                        arr[idx].stakeholder = e.target.checked ? Stakeholder.Contractor : '';
                        setDeclarations(arr);
                      }}
                    />
                    <Typography>{t('Contractor')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                    disabled={isViewMode}
                      checked={item.stakeholder === Stakeholder.AuthorizedRepresentative}
                      onChange={e => {
                        const arr = [...declarations];
                        arr[idx].stakeholder = e.target.checked ? Stakeholder.AuthorizedRepresentative : '';
                        setDeclarations(arr);
                      }}
                    />
                    <Typography>{t('Authorized Representative')}</Typography>
                  </Box>
                </Box>
              </Box>
            )}
            {idx === 0 && (
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField disabled={isViewMode} label={t('Valid Date')} type="date" value={item.validDate} onChange={e => {
                    const arr = [...declarations];
                    arr[idx].validDate = e.target.value;
                    setDeclarations(arr);
                  }} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField disabled={isViewMode} label={t('Time')} type="time" value={item.validTime} onChange={e => {
                    const arr = [...declarations];
                    arr[idx].validTime = e.target.value;
                    setDeclarations(arr);
                  }} fullWidth InputLabelProps={{ shrink: true }} />
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <Typography>{t('Signature')}</Typography>
                {item.signUrl ? <img src={item.signUrl} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />
                  :
                  !isViewMode && (
                  <>
                    <SignaturePad ref={el => declarationPadRefs.current[idx] = el} canvasProps={{ width: 150, height: 40, style: { border: '1px solid #000' } }} onEnd={() => handleDeclarationSignEnd(idx)} />
                    <Box>
                      <Button size="small" onClick={() => {
                        const signaturePad = declarationPadRefs.current[idx];
                        if (signaturePad && signaturePad.getTrimmedCanvas) {
                          const arr = [...declarations];
                          const dataUrl = signaturePad.getTrimmedCanvas().toDataURL();
                          const base64String = (dataUrl);
                          arr[idx].sign = base64String;
                          setDeclarations(arr);
                        }
                      }}>{t('Save')}</Button>
                      <Button size="small" onClick={() => {
                        const signaturePad = declarationPadRefs.current[idx];
                        if (signaturePad && signaturePad.clear) {
                          signaturePad.clear();
                          const arr = [...declarations];
                          arr[idx].sign = '';
                          setDeclarations(arr);
                        }
                      }}>{t('Clear')}</Button>
                    </Box>
                  </>)
                }

                {item.sign && <img src={base64ToDataURL(item.sign)} alt="簽名" style={{ width: 150, height: 40, border: '1px solid #ccc', marginTop: 4 }} />}
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField disabled={isViewMode} label={t('Name')} value={item.name} onChange={e => { const arr = [...declarations]; arr[idx].name = e.target.value; setDeclarations(arr); }} fullWidth />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField disabled={isViewMode} label={t('Position')} value={item.position} onChange={e => { const arr = [...declarations]; arr[idx].position = e.target.value; setDeclarations(arr); }} fullWidth />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField disabled={isViewMode} label={t('Date and Time')} type="datetime-local" value={item.date} onChange={e => { const arr = [...declarations]; arr[idx].date = e.target.value; setDeclarations(arr); }} fullWidth InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Paper>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        {!isViewMode && <Button variant="contained" color="primary" onClick={handleSave}>{t('Save')}</Button>}
        <Button variant="outlined" color="secondary" onClick={handleClose}>{isViewMode ? t('Close') : t('Cancel')}</Button>
      </Box>
    </Box>
  );
};

export default WorkPermitPage;
